import React, { useState, useEffect } from "react";
import {
  Target,
  Calendar,
  TrendingUp,
  Award,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
} from "recharts";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

// ─── helpers ──────────────────────────────────────────────────────────────────

const DAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};
const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const pct = (a, t) => (t > 0 ? Math.round((a / t) * 100) : 0);

const perfColor = (p) =>
  p >= 100 ? "text-green-400" : p >= 75 ? "text-yellow-400" : "text-red-400";

// ─── sub-components ───────────────────────────────────────────────────────────

const KpiCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  colorCls,
  isDarkMode,
  valueClassName,
}) => {
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  return (
    <div className={`rounded-xl border p-4 ${cardBg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm ${mutedText}`}>{title}</span>
        <div className={`p-2 rounded-lg ${colorCls}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className={`text-3xl font-bold ${valueClassName || ""}`}>
        {value}
      </div>
      <div className={`text-sm mt-1 ${mutedText}`}>{subtitle}</div>
    </div>
  );
};

const Insight = ({ label, value, hint, isDarkMode }) => {
  const cardBg = isDarkMode ? "bg-[#13161B]" : "bg-gray-50";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  return (
    <div className={`rounded-lg p-4 ${cardBg}`}>
      <div className={`text-sm ${mutedText}`}>{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {hint && <div className={`text-xs mt-1 ${mutedText}`}>{hint}</div>}
    </div>
  );
};

// ─── SingleMetricPanel ─────────────────────────────────────────────────────────

const SingleMetricPanel = ({ metric, isDarkMode }) => {
  const [expanded, setExpanded] = useState(false);

  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  const today = new Date().toISOString().split("T")[0];
  const todayStat = metric.dailyStats?.find((d) => d.date === today) || {
    target: 0,
    actual: 0,
    percentageAchieved: 0,
  };

  const weekChartData = (metric.thisWeek?.days || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }),
    Target: d.target,
    Actual: d.actual,
  }));

  const p = metric.summary.overallPercentage;

  return (
    <div className={`rounded-xl border ${cardBg} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <span className="text-2xl">{metric.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{metric.label}</p>
          <p className={`text-xs ${mutedText}`}>
            Today: {todayStat.actual} / {todayStat.target} {metric.unit} ·{" "}
            <span className={perfColor(todayStat.percentageAchieved)}>
              {todayStat.percentageAchieved}%
            </span>
          </p>
        </div>
        {/* Mini progress bar */}
        <div className="w-24 h-2 rounded-full bg-gray-700/50 flex-shrink-0 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              todayStat.percentageAchieved >= 100
                ? "bg-green-400"
                : todayStat.percentageAchieved >= 75
                  ? "bg-yellow-400"
                  : "bg-[#427BCA]"
            }`}
            style={{ width: `${Math.min(100, todayStat.percentageAchieved)}%` }}
          />
        </div>
        {expanded ? (
          <ChevronUp size={16} className={mutedText} />
        ) : (
          <ChevronDown size={16} className={mutedText} />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-700/40 pt-4">
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-3">
            <Insight
              label="This week"
              value={`${metric.thisWeek.actual}/${metric.thisWeek.target}`}
              isDarkMode={isDarkMode}
            />
            <Insight
              label={`${metric.unit} overall`}
              value={metric.summary.totalActual}
              isDarkMode={isDarkMode}
            />
            <Insight
              label="Consistency"
              value={`${metric.summary.consistencyRate}%`}
              hint="Days hitting target"
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Weekly defaults */}
          <div>
            <p
              className={`text-xs font-semibold uppercase tracking-wider mb-2 ${mutedText}`}
            >
              Weekly targets
            </p>
            <div className="grid grid-cols-7 gap-1">
              {DAYS.map((d) => {
                const t = metric.weeklyDefaults?.[d] ?? 0;
                return (
                  <div key={d} className="text-center">
                    <div className={`text-xs mb-1 ${mutedText}`}>
                      {DAY_LABELS[d]}
                    </div>
                    <div
                      className={`py-2 rounded text-sm font-semibold ${
                        t > 0
                          ? "bg-[#427BCA]/20 text-[#427BCA]"
                          : isDarkMode
                            ? "bg-gray-700 text-gray-500"
                            : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {t}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bar chart */}
          {weekChartData.length > 0 && (
            <div className="w-full h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekChartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDarkMode ? "#2a2e36" : "#e5e7eb"}
                  />
                  <XAxis
                    dataKey="date"
                    stroke={isDarkMode ? "#94979C" : "#6b7280"}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    stroke={isDarkMode ? "#94979C" : "#6b7280"}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: isDarkMode ? "#0C0E12" : "#fff",
                      border: "1px solid #427BCA",
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Target" fill="#427BCA" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Actual" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const MyAppointmentTargets = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Separate flag: does this user have the NEW multi-metric config or the old one?
  const [useNewApi, setUseNewApi] = useState(true);

  const pageBg = isDarkMode
    ? "bg-[#0A0B0F] text-white"
    : "bg-gray-50 text-gray-900";
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  const load = async () => {
    try {
      setLoading(true);

      // Try new multi-metric endpoint first
      try {
        const res = await apiService.getMyPerformance({ weeks: 4 });
        const d = res.data;

        if (d?.hasTargets && d.teams?.some((t) => t.metrics?.length > 0)) {
          setData({ mode: "multi", ...d });
          setUseNewApi(true);
          return;
        }
      } catch {
        // new endpoint not yet deployed — fall through
      }

      // Fall back to old single-metric endpoint
      const res2 = await apiService.getMyAppointmentTargets({ weeks: 4 });
      setData({ mode: "legacy", ...res2.data?.data });
      setUseNewApi(false);
    } catch (e) {
      console.error("Error loading targets:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${pageBg}`}
      >
        <RefreshCw className="animate-spin text-[#427BCA]" size={32} />
      </div>
    );
  }

  const hasTargets = data?.hasTargets ?? data?.mode === "multi";

  if (!hasTargets) {
    return (
      <div className={`min-h-screen p-6 ${pageBg}`}>
        <div
          className={`max-w-2xl mx-auto rounded-xl border p-12 text-center ${cardBg}`}
        >
          <Target
            size={48}
            className="mx-auto mb-4 text-[#427BCA] opacity-50"
          />
          <h2 className="text-xl font-semibold mb-2">No targets set</h2>
          <p className={mutedText}>
            You're not in a team with performance metrics configured yet. Ask
            your manager to set them up.
          </p>
        </div>
      </div>
    );
  }

  // ── Multi-metric mode ──────────────────────────────────────────────────────
  if (data.mode === "multi") {
    const primaryMetric = data.primaryMetric;
    const allTeams = data.teams || [];

    const today = new Date().toISOString().split("T")[0];
    const primaryToday = primaryMetric?.dailyStats?.find(
      (d) => d.date === today,
    ) || { target: 0, actual: 0, percentageAchieved: 0 };

    const trendData = (primaryMetric?.dailyStats || []).map((d) => ({
      date: new Date(d.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      Target: d.target,
      Actual: d.actual,
      Percent: d.percentageAchieved,
    }));

    return (
      <div className={`min-h-screen p-6 font-inter ${pageBg}`}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Target className="text-[#427BCA]" size={32} />
                My Performance
              </h1>
              <p className={mutedText}>
                {allTeams.map((t) => t.teamName).join(" · ")}
              </p>
            </div>
            <button
              onClick={load}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
            >
              <RefreshCw size={18} />
            </button>
          </div>

          {/* Primary metric hero KPIs */}
          {primaryMetric && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard
                title={`Today's ${primaryMetric.label} target`}
                value={primaryToday.target}
                subtitle={`${primaryToday.actual} done so far`}
                icon={Target}
                colorCls="bg-blue-500/20 text-blue-400"
                isDarkMode={isDarkMode}
              />
              <KpiCard
                title="Today's achievement"
                value={`${primaryToday.percentageAchieved}%`}
                subtitle={
                  primaryToday.actual >= primaryToday.target &&
                  primaryToday.target > 0
                    ? "On target ✓"
                    : `${Math.max(0, primaryToday.target - primaryToday.actual)} to go`
                }
                icon={CheckCircle2}
                colorCls={`${primaryToday.percentageAchieved >= 100 ? "bg-green-500/20 text-green-400" : primaryToday.percentageAchieved >= 75 ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400"}`}
                isDarkMode={isDarkMode}
                valueClassName={perfColor(primaryToday.percentageAchieved)}
              />
              <KpiCard
                title="This week"
                value={`${primaryMetric.thisWeek.actual} / ${primaryMetric.thisWeek.target}`}
                subtitle={
                  primaryMetric.thisWeek.target > 0
                    ? `${pct(primaryMetric.thisWeek.actual, primaryMetric.thisWeek.target)}% achieved`
                    : "No targets this week"
                }
                icon={Calendar}
                colorCls="bg-purple-500/20 text-purple-400"
                isDarkMode={isDarkMode}
              />
              <KpiCard
                title="Overall performance"
                value={`${primaryMetric.summary.overallPercentage}%`}
                subtitle={`${primaryMetric.summary.daysOnTarget}/${primaryMetric.summary.daysWithTarget} days on target`}
                icon={Award}
                colorCls="bg-green-500/20 text-green-400"
                isDarkMode={isDarkMode}
                valueClassName={perfColor(
                  primaryMetric.summary.overallPercentage,
                )}
              />
            </div>
          )}

          {/* All metrics — one collapsible panel each */}
          {allTeams.map((team) => (
            <div key={team.teamId} className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold">{team.teamName}</h2>
                <span className={`text-xs ${mutedText}`}>
                  · {team.metrics.length} metric
                  {team.metrics.length !== 1 ? "s" : ""}
                </span>
              </div>
              {team.metrics.map((m) => (
                <SingleMetricPanel
                  key={m.metricId}
                  metric={m}
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          ))}

          {/* Trend chart for primary metric */}
          {primaryMetric && trendData.length > 0 && (
            <div className={`rounded-xl border p-6 ${cardBg}`}>
              <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
                <TrendingUp size={20} className="text-[#427BCA]" />
                {primaryMetric.label} — Performance Trend
              </h2>
              <p className={`text-sm mb-4 ${mutedText}`}>Past 4 weeks</p>
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDarkMode ? "#2a2e36" : "#e5e7eb"}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={isDarkMode ? "#94979C" : "#6b7280"}
                    />
                    <YAxis
                      yAxisId="left"
                      stroke={isDarkMode ? "#94979C" : "#6b7280"}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke={isDarkMode ? "#94979C" : "#6b7280"}
                      domain={[0, 200]}
                    />
                    <Tooltip
                      contentStyle={{
                        background: isDarkMode ? "#0C0E12" : "#fff",
                        border: "1px solid #427BCA",
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="Target" fill="#427BCA" />
                    <Bar yAxisId="left" dataKey="Actual" fill="#22c55e" />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="Percent"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Legacy single-metric mode (old appointmentTargets) ─────────────────────
  // (unchanged from original MyAppointmentTargets — full implementation below)

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const today = new Date().toISOString().split("T")[0];
  const todayStat = data.dailyStats?.find((d) => d.date === today) || {
    target: 0,
    actual: 0,
    percentageAchieved: 0,
  };

  const weekChartData = (data.thisWeek?.days || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }),
    Target: d.target,
    Actual: d.actual,
  }));

  const trendChartData = (data.dailyStats || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    Target: d.target,
    Actual: d.actual,
    Percent: d.percentageAchieved,
  }));

  const performanceColor = perfColor(data.summary.overallPercentage);

  return (
    <div className={`min-h-screen p-6 font-inter ${pageBg}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Target className="text-[#427BCA]" size={32} />
              My Appointment Targets
            </h1>
            <p className={mutedText}>
              {data.teamName} · {data.teamType?.replace(/_/g, " ")}
            </p>
          </div>
          <button
            onClick={load}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {!data.targets?.enabled && (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400">
            <AlertCircle size={18} />
            Your targets are currently disabled by your manager.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard
            title="Today's Target"
            value={todayStat.target}
            subtitle={`${todayStat.actual} booked so far`}
            icon={Target}
            colorCls="bg-blue-500/20 text-blue-400"
            isDarkMode={isDarkMode}
          />
          <KpiCard
            title="Today's Achievement"
            value={`${todayStat.percentageAchieved}%`}
            subtitle={
              todayStat.actual >= todayStat.target && todayStat.target > 0
                ? "On target ✓"
                : `${Math.max(0, todayStat.target - todayStat.actual)} to go`
            }
            icon={CheckCircle2}
            colorCls={
              todayStat.percentageAchieved >= 100
                ? "bg-green-500/20 text-green-400"
                : todayStat.percentageAchieved >= 75
                  ? "bg-orange-500/20 text-orange-400"
                  : "bg-red-500/20 text-red-400"
            }
            isDarkMode={isDarkMode}
          />
          <KpiCard
            title="This Week"
            value={`${data.thisWeek.actual} / ${data.thisWeek.target}`}
            subtitle={
              data.thisWeek.target > 0
                ? `${pct(data.thisWeek.actual, data.thisWeek.target)}% achieved`
                : "No targets this week"
            }
            icon={Calendar}
            colorCls="bg-purple-500/20 text-purple-400"
            isDarkMode={isDarkMode}
          />
          <KpiCard
            title={`Last ${Math.round((data.dailyStats?.length || 0) / 7)} Weeks`}
            value={`${data.summary.overallPercentage}%`}
            subtitle={`${data.summary.daysOnTarget}/${data.summary.daysWithTarget} days on target`}
            icon={Award}
            colorCls="bg-green-500/20 text-green-400"
            isDarkMode={isDarkMode}
            valueClassName={performanceColor}
          />
        </div>

        <div className={`rounded-xl border p-6 ${cardBg}`}>
          <h2 className="text-lg font-semibold mb-4">My Weekly Targets</h2>
          <div className="grid grid-cols-7 gap-2">
            {days.map((d) => {
              const t = data.targets?.weeklyDefaults?.[d] || 0;
              return (
                <div key={d} className="text-center">
                  <div className={`text-xs font-medium mb-1 ${mutedText}`}>
                    {DAY_LABELS[d]}
                  </div>
                  <div
                    className={`py-2 px-2 rounded font-semibold ${t > 0 ? "bg-[#427BCA]/20 text-[#427BCA]" : isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                  >
                    {t}
                  </div>
                </div>
              );
            })}
          </div>
          <p className={`text-xs mt-3 ${mutedText}`}>
            Minimum daily appointment slots set by your manager.
          </p>
        </div>

        <div className={`rounded-xl border p-6 ${cardBg}`}>
          <h2 className="text-lg font-semibold mb-4">
            This Week — Target vs Actual
          </h2>
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDarkMode ? "#2a2e36" : "#e5e7eb"}
                />
                <XAxis
                  dataKey="date"
                  stroke={isDarkMode ? "#94979C" : "#6b7280"}
                />
                <YAxis stroke={isDarkMode ? "#94979C" : "#6b7280"} />
                <Tooltip
                  contentStyle={{
                    background: isDarkMode ? "#0C0E12" : "#fff",
                    border: "1px solid #427BCA",
                    borderRadius: 8,
                  }}
                />
                <Legend />
                <Bar dataKey="Target" fill="#427BCA" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Actual" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`rounded-xl border p-6 ${cardBg}`}>
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <TrendingUp size={20} className="text-[#427BCA]" />
            Performance Trend
          </h2>
          <p className={`text-sm mb-4 ${mutedText}`}>
            Past {Math.round((data.dailyStats?.length || 0) / 7)} weeks
          </p>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDarkMode ? "#2a2e36" : "#e5e7eb"}
                />
                <XAxis
                  dataKey="date"
                  stroke={isDarkMode ? "#94979C" : "#6b7280"}
                />
                <YAxis
                  yAxisId="left"
                  stroke={isDarkMode ? "#94979C" : "#6b7280"}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={isDarkMode ? "#94979C" : "#6b7280"}
                  domain={[0, 200]}
                />
                <Tooltip
                  contentStyle={{
                    background: isDarkMode ? "#0C0E12" : "#fff",
                    border: "1px solid #427BCA",
                    borderRadius: 8,
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="Target" fill="#427BCA" />
                <Bar yAxisId="left" dataKey="Actual" fill="#22c55e" />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="Percent"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`rounded-xl border p-6 ${cardBg}`}>
          <h2 className="text-lg font-semibold mb-4">Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Insight
              label="Total appointments booked"
              value={data.summary.totalActual}
              isDarkMode={isDarkMode}
            />
            <Insight
              label="Total target"
              value={data.summary.totalTarget}
              isDarkMode={isDarkMode}
            />
            <Insight
              label="Consistency rate"
              value={`${data.summary.consistencyRate}%`}
              hint="Days you hit target ÷ days with target"
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAppointmentTargets;
